// timeUtils.js - Centralized time conversion utilities

/**
 * Converts a China time string to a Nairobi Date object
 * @param {string} chinaTimeString - Time string in format "YYYY-MM-DDTHH:mm:ss"
 * @returns {Date|null} - Date object in Nairobi time, or null if invalid
 */
export const chinaToNairobiDate = (chinaTimeString) => {
  if (!chinaTimeString) return null;

  try {
    const [datePart, timePart] = chinaTimeString.split("T");
    const [year, month, day] = datePart.split("-").map(Number);
    const [hour, minute, second] = timePart.split(":").map(Number);

    // Create a date representing the China time as UTC+8
    const chinaDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
    
    // Subtract 8 hours to convert China time (UTC+8) to actual UTC
    chinaDate.setHours(chinaDate.getHours() - 8);

    return chinaDate;
  } catch (error) {
    console.error("Error converting China time to Nairobi:", error);
    return null;
  }
};

/**
 * Converts a China time string to a formatted Nairobi time string for display
 * @param {string} chinaTimeString - Time string in format "YYYY-MM-DDTHH:mm:ss"
 * @returns {string} - Formatted Nairobi time string
 */
export const chinaToNairobiDisplay = (chinaTimeString) => {
  if (!chinaTimeString) return "—";

  const utcDate = chinaToNairobiDate(chinaTimeString);
  if (!utcDate) return "—";

  // Format for Nairobi time (UTC+3)
  const nairobiTime = new Intl.DateTimeFormat("en-KE", {
    timeZone: "Africa/Nairobi",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(utcDate);

  return nairobiTime;
};

/**
 * Filter orders based on Nairobi time (converted from China time)
 * @param {Array} orders - Array of order objects with pBorrowtime in China time
 * @param {Object} dateRange - Object with start and end Date objects in Nairobi time
 * @returns {Array} - Filtered orders
 */
export const filterOrdersByNairobiTime = (orders, dateRange) => {
  if (!orders || orders.length === 0) return [];
  if (!dateRange || !dateRange.start || !dateRange.end) return orders;

  return orders.filter(order => {
    const nairobiDate = chinaToNairobiDate(order.pBorrowtime);
    if (!nairobiDate) return false;

    // Compare the converted Nairobi date with the date range
    return nairobiDate >= dateRange.start && nairobiDate <= dateRange.end;
  });
};

/**
 * Get all orders and add a Nairobi timestamp field to each
 * This is useful for sorting and grouping by Nairobi dates
 * @param {Array} orders - Array of order objects
 * @returns {Array} - Orders with added nairobiTimestamp field
 */
export const addNairobiTimestamps = (orders) => {
  if (!orders || orders.length === 0) return [];

  return orders.map(order => ({
    ...order,
    nairobiTimestamp: chinaToNairobiDate(order.pBorrowtime)
  }));
};